import { Alert, PrimaryButton } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import withDispensaryAccount, {
  WithDispensaryAccountFC,
} from '../../../../../../../hoc/withDispensaryAccount'
import { useLogAndCaptureLeftResult } from '../../../../../../../hooks/useLogAndCaptureLeftResult'
import { useResultWithLeftEffect } from '../../../../../../../hooks/useResultWithLeftEffect'
import {
  sanityDispensaryUpdate$,
  setUpdateDispensaryInput,
  useSanityDispensaryUpdate,
} from '../../../../../../../lib/sanity-dispensaries/manage/dispensary'
import {
  addProductToList,
  moveProductDownList,
  moveProductUpList,
  Product,
  removeProductFromList,
  useSanityManageFeaturedProducts,
} from '../../../../../../../lib/sanity-dispensaries/manage/featured-products'
import { constEmptyResult, constUndefined } from '../../../../../../../utils/constant'
import ContactSupportMessage from '../../../../../../ContactSupportMessage'
import { RequiredTextField } from '../../../../../../form/RequiredFields'
import Spacer from '../../../../../../Spacer'
import Spinner from '../../../../../../Spinner'
import { ProductList } from '../ProductList'
import { ProductListSearch } from '../ProductListSearch'
import { ProductListSuggestions } from '../ProductListSuggestions'
import * as Styled from './styled'

export interface FeaturedProductsFormValues {
  title: string
}

const pluckData = <T,>({ data }: { data: T }) => data

const CustomizeFeaturedProductsContent: React.FC = () => {
  const [sanityProductsLeft, sanityProducts] = useResultWithLeftEffect(
    useSanityManageFeaturedProducts(),
    pluckData,
    constUndefined,
  )

  const [products, setProducts] = useState<Product[]>()
  const [productsDirty, setProductsDirty] = useState(false)

  const addProduct = useCallback((productToAdd: Product) => {
    setProductsDirty(true)
    setProducts(addProductToList(productToAdd))
  }, [])

  const moveProductUp = useCallback((productToMove: Product) => {
    setProductsDirty(true)
    setProducts(moveProductUpList(productToMove))
  }, [])

  const moveProductDown = useCallback((productToMove: Product) => {
    setProductsDirty(true)
    setProducts(moveProductDownList(productToMove))
  }, [])

  const removeProduct = useCallback((productToRemove: Product) => {
    setProductsDirty(true)
    setProducts(removeProductFromList(productToRemove))
  }, [])

  const defaultValues = useMemo((): FeaturedProductsFormValues => {
    return {
      title: sanityProducts?.productList?.title ?? 'Featured Products',
    }
  }, [sanityProducts?.productList?.title])
  const methods = useForm<FeaturedProductsFormValues>({ defaultValues })
  const {
    formState: { isDirty: formDirty, isSubmitting },
    handleSubmit,
    reset,
  } = methods

  useEffect(() => {
    // reset form after submit
    reset(defaultValues)
  }, [defaultValues, reset])

  const onSubmit: SubmitHandler<FeaturedProductsFormValues> = (
    formValues: FeaturedProductsFormValues,
  ) => {
    setUpdateDispensaryInput({
      method: 'PUT',
      input: {
        productList: {
          _key: sanityProducts?.productList?._key,
          title: formValues.title ?? 'Featured Products',
          skus: products?.map((product) => product.sku) || [],
        },
      },
    })
  }

  const { loading: updateLoading, result: updateResult } = useSanityDispensaryUpdate()
  useLogAndCaptureLeftResult(updateResult ?? constEmptyResult())

  useEffect(() => {
    if (sanityProducts?.loading) {
      return
    }
    setProducts(sanityProducts?.products ?? [])
  }, [sanityProducts])

  useEffect(() => {
    const subscription = sanityDispensaryUpdate$.subscribe(() => setProductsDirty(false))
    return () => subscription.unsubscribe()
  }, [])

  if (sanityProductsLeft || !sanityProducts) {
    return (
      <div>
        <Alert type="error">
          An error occurred retrieving your dispensary customization, and we have been notified.{' '}
          <ContactSupportMessage />
        </Alert>
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Styled.HeaderWithButton>
          <h3>Customize Featured Products</h3>
          <PrimaryButton type="submit" disabled={(!formDirty && !productsDirty) || updateLoading}>
            {isSubmitting || updateLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : formDirty || productsDirty ? (
              'Publish Changes'
            ) : (
              'No Changes to Publish'
            )}
          </PrimaryButton>
        </Styled.HeaderWithButton>
        {updateResult?._tag === 'Left' && (
          <Alert type="error">
            An error occurred updating your dispensary, and we have been notified.{' '}
            <ContactSupportMessage />
          </Alert>
        )}
        {sanityProducts && sanityProducts.unavailableSkus.length > 0 && (
          <Alert type="attention">
            Products with the following skus are no longer available and have been removed from your
            list: {sanityProducts.unavailableSkus.join(', ')}
          </Alert>
        )}
        <RequiredTextField label="Product List Title" name="title" />
        <Spacer size={3} />
        <Styled.SmallHeader>Add New Product:</Styled.SmallHeader>
        <ProductListSearch onSelect={addProduct} />
        <Styled.Heading>Your Products</Styled.Heading>
        {!products && sanityProducts?.loading && <Spinner loading />}
        {products && products.length === 0 && (
          <div>
            Add a product by searching above or selecting below. If no products are selected, we
            will default to the suggestions below.
          </div>
        )}
        <ProductList
          products={products}
          moveProductUp={moveProductUp}
          moveProductDown={moveProductDown}
          removeProduct={removeProduct}
        />
        <Styled.Heading>Suggested Products</Styled.Heading>
        <ProductListSuggestions
          addedProducts={products}
          onAdd={addProduct}
          onRemove={removeProduct}
        />
      </form>
    </FormProvider>
  )
}

const CustomizeFeaturedProducts: WithDispensaryAccountFC = () => (
  <Subscribe fallback={<Spinner loading />}>
    <CustomizeFeaturedProductsContent />
  </Subscribe>
)

export default withDispensaryAccount(CustomizeFeaturedProducts)
